import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import "./ExtendedViewPage.scss";
import {
  FiChevronLeft,
  FiChevronRight,
  FiSettings,
  FiChevronDown,
} from "react-icons/fi";
import { getBothCourtCasesDetailsByCourtRef } from "../../services/api/CasesApi";
import { getUserData } from "../../services/api/AuthApi";
import { getBranchByBranchID } from "../../services/api/BranchApi";
import {
  getTasksAssignedByUser,
  getTasksAssignedToUser,
} from "../../services/api/TaskApi";
import draftToHtml from 'draftjs-to-html';
import { getNoteByUser } from "../../services/api/NoteApi";
import { getDisplayBoardData } from "../../services/api/DisplayBoardApi";
import ExportModal from "../../components/ExportModal/ExportModal";
import { exportDashboardToPDF } from "../../components/exportDashboardToPDF";
import { FaTableList } from "react-icons/fa6";
import { IoGrid } from "react-icons/io5";
import { useLoading } from "../../context/LoadingContext";

const ExtendedViewPage = () => {
  const { state } = useLocation();
  const { date: paramDate } = useParams();
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();

  const fullData = state?.fullData || {};
  const initialDate = state?.selectedDate || paramDate;

  const today = new Date();
  const getTodayStr = () => today.toISOString().split("T")[0];

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [activeTab, setActiveTab] = useState("hearings");
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [dateOffset, setDateOffset] = useState(0);
  const [viewMode, setViewMode] = useState("table");

  const [taskDetails, setTaskDetails] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [dashboardData, setDashboardData] = useState({});

  // Options dropdown state
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Sorting state
  const [hearingSortBy, setHearingSortBy] = useState("courtRef_asc"); // Default sort for hearings
  const [taskSortBy, setTaskSortBy] = useState("taskName_asc"); // Default sort for tasks

  const [visibleFields, setVisibleFields] = useState({
    hearings: {
      "Basic Information": true,
      "Case Details": true,
      "Case Status": true,
    },
    tasks: {
      "Task Name": true,
      "Court Ref": true,
      "Assigned By": true,
      "Assigned To": true,
      Branch: false,
      Description: true,
      "Due Date": true,
      Status: true,
      "Recurring Task": true,
      Document: true,
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptionsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (initialDate) {
      const initialDateObj = new Date(initialDate);
      if (!isNaN(initialDateObj.getTime())) {
        setCurrentMonth(initialDateObj.getMonth());
        setCurrentYear(initialDateObj.getFullYear());
        const dayOfMonth = initialDateObj.getDate();
        const targetOffset = Math.max(0, dayOfMonth - 6);
        setDateOffset(targetOffset);
      }
    }
  }, [initialDate]);

  const selectedDayData = fullData[selectedDate] || {};

  const generateDates = () => {
    const dates = [];
    const startDate = new Date(currentYear, currentMonth, 1 + dateOffset);

    for (let i = 0; i < 12; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  const dates = generateDates();

  const formatDate = (date) => {
    const day = date.getDate();
    const month = date
      .toLocaleString("default", { month: "short" })
      .toUpperCase();
    const weekday = date
      .toLocaleString("default", { weekday: "short" })
      .toUpperCase();

    const year = date.getFullYear();
    const monthNum = String(date.getMonth() + 1).padStart(2, "0");
    const dayNum = String(day).padStart(2, "0");
    const fullDate = `${year}-${monthNum}-${dayNum}`;

    return { day, month, weekday, fullDate };
  };

  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
  };

  const handleTodayClick = () => {
    const todayStr = getTodayStr();
    setSelectedDate(todayStr);
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    const todayDay = today.getDate();
    const targetOffset = Math.max(0, todayDay - 6);
    setDateOffset(targetOffset);
  };

  const handlePrevDates = () => {
    setDateOffset((prev) => Math.max(0, prev - 10));
  };

  const handleNextDates = () => {
    setDateOffset((prev) => prev + 10);
  };

  const handleMonthChange = (e) => {
    setCurrentMonth(parseInt(e.target.value));
    setDateOffset(0);
  };

  const handleYearChange = (e) => {
    setCurrentYear(parseInt(e.target.value));
    setDateOffset(0);
  };
  const handleExportClick = () => {
    setShowExportModal(true);
  };

  // Updated to handle orientation parameter
  const handleExport = (dataToExport, orientation) => {
    exportDashboardToPDF(dataToExport, orientation);
  };

  const fetchTaskDetails = useCallback(async (tasks) => {
    //showLoading('Loading task details...');
    const details = {};

    try {
      for (const task of tasks) {
        const taskId = task._id;
        details[taskId] = {
          caseData: null,
          assignedByName: null,
          assignedToNames: [],
          branchName: null,
          loading: true,
        };

        // Fetch case data by court ref
        if (task.courtRef) {
          try {
            const caseResponse = await getBothCourtCasesDetailsByCourtRef(
              task.courtRef
            );
            details[taskId].caseData = caseResponse.data?.case;
          } catch (error) {
            console.error(
              `Error fetching case data for ${task.courtRef}:`,
              error
            );
          }
        }

        // Fetch assigned by user name
        if (task.assignedBy) {
          try {
            const userResponse = await getUserData(task.assignedBy);
            details[taskId].assignedByName =
              userResponse.data?.name ||
              userResponse.data?.username ||
              "Unknown User";
          } catch (error) {
            console.error(
              `Error fetching user data for ${task.assignedBy}:`,
              error
            );
          }
        }

        // Fetch assigned to user names
        if (task.assignedTo && Array.isArray(task.assignedTo)) {
          const assignedToNames = [];
          for (const userId of task.assignedTo) {
            try {
              const userResponse = await getUserData(userId);
              assignedToNames.push(
                userResponse.data?.name ||
                  userResponse.data?.username ||
                  "Unknown User"
              );
            } catch (error) {
              console.error(`Error fetching user data for ${userId}:`, error);
              assignedToNames.push("Unknown User");
            }
          }
          details[taskId].assignedToNames = assignedToNames;
        }

        // Fetch branch name
        if (task.branchId) {
          try {
            const branchResponse = await getBranchByBranchID(task.branchId);
            details[taskId].branchName =
              branchResponse.data?.result?.branchData?.branchName ||
              "Unknown Branch";
          } catch (error) {
            console.error(
              `Error fetching branch data for ${task.branchId}:`,
              error
            );
          }
        }

        details[taskId].loading = false;
      }
    } catch (error) {
      console.error("Error fetching task details:", error);
    }

    setTaskDetails(details);
    hideLoading();
  }, []);

  useEffect(() => {
    if (activeTab === "tasks" && selectedDayData.tasks?.length > 0) {
      fetchTaskDetails(selectedDayData.tasks);
    }
  }, [selectedDate, activeTab, selectedDayData.tasks, fetchTaskDetails]); // Added selectedDayData.tasks to dependencies

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [dashboard, assignedBy, assignedTo, notes] = await Promise.all([
          getDisplayBoardData(),
          getTasksAssignedByUser(),
          getTasksAssignedToUser(),
          getNoteByUser(),
        ]);

        setDashboardData({
          ...dashboard.data,
          assignedByTasks: assignedBy.data,
          assignedToTasks: assignedTo.data,
          notes: notes.data.notes,
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, []);

  const getTaskDetail = (taskId) => {
    return (
      taskDetails[taskId] || {
        caseData: null,
        assignedByName: null,
        assignedToNames: [],
        branchName: null,
        loading: true,
      }
    );
  };

  const handleFieldToggle = (fieldName) => {
    setVisibleFields((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [fieldName]: !prev[activeTab][fieldName],
      },
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(visibleFields[activeTab]).every(
      (value) => value
    );
    setVisibleFields((prev) => ({
      ...prev,
      [activeTab]: Object.keys(prev[activeTab]).reduce(
        (acc, key) => ({
          ...acc,
          [key]: !allSelected,
        }),
        {}
      ),
    }));
  };

  const isFieldVisible = (fieldName) => {
    return visibleFields[activeTab][fieldName];
  };

  const handleTaskClick = (task) => {
    navigate(`/home/tasks/viewtask/${task._id}`, {
      state: {
        parties: taskDetails[task._id]?.caseData?.parties || null
      }
    });
  };

  const handleCourtRefClick = (e, courtRef) => {
    e.stopPropagation(); // Prevent the row click from triggering
    navigate(`/home/cases/casedetails/${courtRef}`);
  };

  const handleHearingClick = (hearing) => {
    navigate(`/home/cases/casedetails/${hearing.courtRef}`);
  };

  const renderTaskField = (fieldName, task, details, index) => {
    if (!isFieldVisible(fieldName)) return null;

    switch (fieldName) {
      case "Task Name":
        return (
          <div className="ext-cal-task-row" key="taskName">
            <span className="ext-cal-task-label">Task Name:</span>
            <span className="ext-cal-task-value">{task.taskName}</span>
          </div>
        );
      case "Court Ref":
        return (
          <div className="ext-cal-task-row" key="courtRef">
            <span className="ext-cal-task-label">Court Ref:</span>
            <span 
              className="ext-cal-task-value"
              // onClick={(e) => handleCourtRefClick(e, task.courtRef)}
              
            >
              {task.courtRef} - ({details?.caseData?.parties || "Not Available"}
              )
            </span>
          </div>
        );
      case "Assigned By":
        return (
          <div className="ext-cal-task-row" key="assignedBy">
            <span className="ext-cal-task-label">Assigned By:</span>
            <span className="ext-cal-task-value">
              {details.loading
                ? "Loading..."
                : details.assignedByName || task.assignedBy}
            </span>
          </div>
        );
      case "Assigned To":
        return (
          <div className="ext-cal-task-row" key="assignedTo">
            <span className="ext-cal-task-label">Assigned To:</span>
            <span className="ext-cal-task-value">
              {details.loading
                ? "Loading..."
                : details.assignedToNames.length > 0
                ? details.assignedToNames.join(", ")
                : Array.isArray(task.assignedTo)
                ? task.assignedTo.join(", ")
                : task.assignedTo}
            </span>
          </div>
        );
      case "Branch":
        return (
          <div className="ext-cal-task-row" key="branch">
            <span className="ext-cal-task-label">Branch:</span>
            <span className="ext-cal-task-value">
              {details.loading
                ? "Loading..."
                : details.branchName || task.branchId}
            </span>
          </div>
        );
      case "Description":
        return (
          <div className="ext-cal-task-row" key="description">
            <span className="ext-cal-task-label">Description:</span>
            <span className="ext-cal-task-value">
              {" "}
              {task.description ? (
                (() => {
                  let html = "";
                  try {
                    const raw =
                      typeof task.description === "string"
                        ? JSON.parse(task.description)
                        : task.description;
                    if (raw && raw.blocks) {
                      html = draftToHtml(raw);
                    } else {
                      html = task.description;
                    }
                  } catch (e) {
                    html = task.description;
                  }
                  // Fix anchor tags to always use absolute URLs
                  let fixedHtml = html.replace(
                    /<a ([^>]*href=["'])(?!https?:\/\/)([^"'>]+)(["'][^>]*)>/gi,
                    "<a $1https://$2$3>"
                  );

                  fixedHtml = fixedHtml.replace(
                    /<a /gi,
                    '<a target="_blank" rel="noopener noreferrer" '
                  );
                  return (
                    <div dangerouslySetInnerHTML={{ __html: fixedHtml }} />
                  );
                })()
              ) : (
                <p>No description provided</p>
              )}
            </span>
          </div>
        );
      case "Due Date":
        return (
          <div className="ext-cal-task-row" key="dueDate">
            <span className="ext-cal-task-label">Due Date:</span>
            <span className="ext-cal-task-value">
              {task.dueDate ? new Date(task.dueDate).toLocaleString() : "N/A"}
            </span>
          </div>
        );
      case "Status":
        return (
          <div className="ext-cal-task-row" key="status">
            <span className="ext-cal-task-label">Status:</span>
            <span className="ext-cal-task-value">
              {task.isCompleted ? "Completed" : "Pending"}
            </span>
          </div>
        );
      case "Recurring Task":
        return task.recurringTask ? (
          <div className="ext-cal-task-row" key="recurringTask">
            <span className="ext-cal-task-label">Recurring Task:</span>
            <span className="ext-cal-task-value">
              {task.recurringTask.times} times per {task.recurringTask.in}
            </span>
          </div>
        ) : null;
      case "Document":
        return task.document ? (
          <div className="ext-cal-task-row" key="document">
            <span className="ext-cal-task-label">Document:</span>
            <span className="ext-cal-task-value">
              <a
                href={task.document}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                View Document
              </a>
            </span>
          </div>
        ) : null;
      default:
        return null;
    }
  };

  // --- Dynamic Sort Options Generation ---
  const getSortOptions = useMemo(() => {
    if (activeTab === "hearings") {
      const options = [];
      if (visibleFields.hearings["Basic Information"]) {
        options.push({ value: "courtRef", label: "Court Ref" });
        options.push({ value: "parties", label: "Parties" });
        options.push({ value: "nextHearingDate", label: "Next Hearing Date" });
      }
      if (visibleFields.hearings["Case Details"]) {
        options.push({ value: "filingDate", label: "Filing Date" });
        options.push({ value: "registrationDate", label: "Registration Date" });
      }
      if (visibleFields.hearings["Case Status"]) {
        options.push({
          value: "firstHearingDate",
          label: "First Hearing Date",
        });
        options.push({
          value: "caseStatusNextHearingDate",
          label: "Case Status Next Hearing Date",
        }); // Renamed to avoid clash
      }
      return options.flatMap((option) => [
        { value: `${option.value}_asc`, label: `${option.label} (Asc)` },
        { value: `${option.value}_desc`, label: `${option.label} (Desc)` },
      ]);
    } else {
      // activeTab === 'tasks'
      const options = [];
      if (visibleFields.tasks["Task Name"])
        options.push({ value: "taskName", label: "Task Name" });
      if (visibleFields.tasks["Due Date"])
        options.push({ value: "dueDate", label: "Due Date" });
      if (visibleFields.tasks["Recurring Task"])
        options.push({ value: "recurringTask", label: "Recurring Task" });
      // Add other visible task fields that you want to enable sorting for here
      if (visibleFields.tasks["Court Ref"])
        options.push({ value: "courtRef", label: "Court Ref" });
      //if (visibleFields.tasks['Assigned By']) options.push({ value: 'assignedBy', label: 'Assigned By' });
      //if (visibleFields.tasks['Status']) options.push({ value: 'status', label: 'Status' });
      //if (visibleFields.tasks['Branch']) options.push({ value: 'branch', label: 'Branch' });

      return options.flatMap((option) => [
        { value: `${option.value}_asc`, label: `${option.label} (Asc)` },
        { value: `${option.value}_desc`, label: `${option.label} (Desc)` },
      ]);
    }
  }, [activeTab, visibleFields]); // Recalculate when activeTab or visibleFields change

  // --- Sorting Logic ---
  const sortedHearings = useMemo(() => {
    return [...(selectedDayData.hearings || [])].sort((a, b) => {
      const [sortByField, sortOrder] = hearingSortBy.split("_");
      let comparison = 0;

      switch (sortByField) {
        case "courtRef":
          comparison = (a.courtRef || "").localeCompare(b.courtRef || "");
          break;
        case "parties":
          comparison = (a.caseDate?.parties || "").localeCompare(
            b.caseDate?.parties || ""
          );
          break;
        case "nextHearingDate":
          const dateA_nhd = a.nextHearingDate
            ? new Date(a.nextHearingDate).getTime()
            : 0;
          const dateB_nhd = b.nextHearingDate
            ? new Date(b.nextHearingDate).getTime()
            : 0;
          comparison = dateA_nhd - dateB_nhd;
          break;
        case "filingDate":
          const dateA_fd = a.caseDate?.caseDetails?.["Filing Date"]
            ? new Date(a.caseDate["caseDetails"]["Filing Date"]).getTime()
            : 0;
          const dateB_fd = b.caseDate?.caseDetails?.["Filing Date"]
            ? new Date(b.caseDate["caseDetails"]["Filing Date"]).getTime()
            : 0;
          comparison = dateA_fd - dateB_fd;
          break;
        case "registrationDate":
          const dateA_rd = a.caseDate?.caseDetails?.["Registration Date"]
            ? new Date(a.caseDate["caseDetails"]["Registration Date"]).getTime()
            : 0;
          const dateB_rd = b.caseDate?.caseDetails?.["Registration Date"]
            ? new Date(b.caseDate["caseDetails"]["Registration Date"]).getTime()
            : 0;
          comparison = dateA_rd - dateB_rd;
          break;
        case "firstHearingDate":
          const dateA_fhd = a.caseDate?.caseStatus?.["First Hearing Date"]
            ? new Date(a.caseDate["caseStatus"]["First Hearing Date"]).getTime()
            : 0;
          const dateB_fhd = b.caseDate?.caseStatus?.["First Hearing Date"]
            ? new Date(b.caseDate["caseStatus"]["First Hearing Date"]).getTime()
            : 0;
          comparison = dateA_fhd - dateB_fhd;
          break;
        case "caseStatusNextHearingDate": // Use the renamed field
          const dateA_csnhd = a.caseDate?.caseStatus?.["Next Hearing Date"]
            ? new Date(a.caseDate["caseStatus"]["Next Hearing Date"]).getTime()
            : 0;
          const dateB_csnhd = b.caseDate?.caseStatus?.["Next Hearing Date"]
            ? new Date(b.caseDate["caseStatus"]["Next Hearing Date"]).getTime()
            : 0;
          comparison = dateA_csnhd - dateB_csnhd;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === "desc" ? -comparison : comparison;
    });
  }, [selectedDayData.hearings, hearingSortBy]);

  const sortedTasks = useMemo(() => {
    return [...(selectedDayData.tasks || [])].sort((a, b) => {
      const detailsA = taskDetails[a._id] || {};
      const detailsB = taskDetails[b._id] || {};
      const [sortByField, sortOrder] = taskSortBy.split("_");
      let comparison = 0;

      switch (sortByField) {
        case "taskName":
          comparison = (a.taskName || "").localeCompare(b.taskName || "");
          break;
        case "dueDate":
          const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
          const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case "recurringTask":
          const recurringA = a.recurringTask
            ? `${a.recurringTask.times} ${a.recurringTask.in}`
            : "";
          const recurringB = b.recurringTask
            ? `${b.recurringTask.times} ${b.recurringTask.in}`
            : "";
          comparison = recurringA.localeCompare(recurringB);
          break;
        case "courtRef":
          comparison = (a.courtRef || "").localeCompare(b.courtRef || "");
          break;
        case "assignedBy":
          comparison = (detailsA.assignedByName || "").localeCompare(
            detailsB.assignedByName || ""
          );
          break;
        case "status":
          const statusA = a.isCompleted ? "Completed" : "Pending";
          const statusB = b.isCompleted ? "Completed" : "Pending";
          comparison = statusA.localeCompare(statusB);
          break;
        case "branch":
          comparison = (detailsA.branchName || "").localeCompare(
            detailsB.branchName || ""
          );
          break;
        default:
          comparison = 0;
      }

      return sortOrder === "desc" ? -comparison : comparison;
    });
  }, [selectedDayData.tasks, taskSortBy, taskDetails]);

  // Render hearings based on view mode
  const renderHearings = () => {
    if (sortedHearings?.length === 0) {
      return (
        <p className="ext-cal-empty-message">No hearings for this date.</p>
      );
    }

    if (viewMode === "table") {
      return (
        <div className="ext-cal-table-container">
          <table className="ext-cal-table">
            <thead>
              <tr>
                <th>#</th>
                {isFieldVisible("Basic Information") && (
                  <>
                    <th>Court Ref</th>
                    <th>Parties</th>
                    <th>Next Hearing Date</th>
                    <th>Source</th>
                  </>
                )}
                {isFieldVisible("Case Details") && (
                  <>
                    <th>Filing Date</th>
                    <th>Registration Date</th>
                    <th>Case Type</th>
                    <th>Stage</th>
                  </>
                )}
                {isFieldVisible("Case Status") && (
                  <>
                    <th>First Hearing Date</th>
                    <th>Status Next Hearing Date</th>
                    <th>Last Order Date</th>
                    <th>Status</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedHearings.map((hearing, index) => (
                <tr 
                  key={index}
                  onClick={() => handleHearingClick(hearing)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{index + 1}</td>
                  {isFieldVisible("Basic Information") && (
                    <>
                      <td>{hearing.courtRef}</td>
                      <td>{hearing.caseDate?.parties || "N/A"}</td>
                      <td>{hearing.nextHearingDate}</td>
                      <td>{hearing.source}</td>
                    </>
                  )}
                  {isFieldVisible("Case Details") && (
                    <>
                      <td>
                        {hearing.caseDate?.caseDetails?.["Filing Date"] ||
                          "N/A"}
                      </td>
                      <td>
                        {hearing.caseDate?.caseDetails?.["Registration Date"] ||
                          "N/A"}
                      </td>
                      <td>
                        {hearing.caseDate?.caseDetails?.["Case Type"] || "N/A"}
                      </td>
                      <td>
                        {hearing.caseDate?.caseDetails?.["Stage"] || "N/A"}
                      </td>
                    </>
                  )}
                  {isFieldVisible("Case Status") && (
                    <>
                      <td>
                        {hearing.caseDate?.caseStatus?.["First Hearing Date"] ||
                          "N/A"}
                      </td>
                      <td>
                        {hearing.caseDate?.caseStatus?.["Next Hearing Date"] ||
                          "N/A"}
                      </td>
                      <td>
                        {hearing.caseDate?.caseStatus?.["Last Order Date"] ||
                          "N/A"}
                      </td>
                      <td>
                        {hearing.caseDate?.caseStatus?.["Status"] || "N/A"}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Card view (original)
    return (
      <div className="ext-cal-hearings-list">
        {sortedHearings.map((hearing, index) => (
          <div 
            key={index} 
            className="ext-cal-hearing-item"
          >
            <div className="ext-cal-hearing-section">
              {isFieldVisible("Basic Information") && (
                <>
                  <h4 className="ext-cal-hearing-section-title basic-info">
                    Basic Information #{index + 1}
                  </h4>
                  <div className="ext-cal-hearing-row">
                    <span className="ext-cal-hearing-label">Court Ref:</span>
                    <span className="ext-cal-hearing-value">
                      {hearing.courtRef}
                    </span>
                  </div>
                  <div className="ext-cal-hearing-row">
                    <span className="ext-cal-hearing-label">Parties:</span>
                    <span className="ext-cal-hearing-value">
                      {hearing.caseDate?.parties || "N/A"}
                    </span>
                  </div>
                  <div className="ext-cal-hearing-row">
                    <span className="ext-cal-hearing-label">
                      Next Hearing Date:
                    </span>
                    <span className="ext-cal-hearing-value">
                      {hearing.nextHearingDate}
                    </span>
                  </div>
                  <div className="ext-cal-hearing-row">
                    <span className="ext-cal-hearing-label">Source:</span>
                    <span className="ext-cal-hearing-value">
                      {hearing.source}
                    </span>
                  </div>
                </>
              )}

              {isFieldVisible("Case Details") &&
                hearing.caseDate?.caseDetails && (
                  <>
                    <h4 className="ext-cal-hearing-section-title">
                      Case Details
                    </h4>
                    {Object.entries(hearing.caseDate.caseDetails).map(
                      ([key, value]) => (
                        <div key={key} className="ext-cal-hearing-row">
                          <span className="ext-cal-hearing-label">{key}:</span>
                          <span className="ext-cal-hearing-value">{value}</span>
                        </div>
                      )
                    )}
                  </>
                )}

              {isFieldVisible("Case Status") &&
                hearing.caseDate?.caseStatus && (
                  <>
                    <h4 className="ext-cal-hearing-section-title">
                      Case Status
                    </h4>
                    {Object.entries(hearing.caseDate.caseStatus).map(
                      ([key, value]) => (
                        <div key={key} className="ext-cal-hearing-row">
                          <span className="ext-cal-hearing-label">{key}:</span>
                          <span className="ext-cal-hearing-value">{value}</span>
                        </div>
                      )
                    )}
                  </>
                )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render tasks based on view mode
  const renderTasks = () => {
    if (sortedTasks?.length === 0) {
      return <p className="ext-cal-empty-message">No tasks for this date.</p>;
    }

    if (viewMode === "table") {
      return (
        <div className="ext-cal-table-container">
          <table className="ext-cal-table">
            <thead>
              <tr>
                <th>#</th>
                {isFieldVisible("Task Name") && <th>Task Name</th>}
                {isFieldVisible("Court Ref") && <th>Court Ref</th>}
                {isFieldVisible("Assigned By") && <th>Assigned By</th>}
                {isFieldVisible("Assigned To") && <th>Assigned To</th>}
                {isFieldVisible("Branch") && <th>Branch</th>}
                {isFieldVisible("Description") && <th>Description</th>}
                {isFieldVisible("Due Date") && <th>Due Date</th>}
                {isFieldVisible("Status") && <th>Status</th>}
                {isFieldVisible("Recurring Task") && <th>Recurring Task</th>}
                {isFieldVisible("Document") && <th>Document</th>}
              </tr>
            </thead>
            <tbody>
              {sortedTasks.map((task, index) => {
                const details = getTaskDetail(task._id);
                return (
                  <tr 
                    key={task._id} 
                    onClick={() => handleTaskClick(task)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{index + 1}</td>
                    {isFieldVisible("Task Name") && <td>{task.taskName}</td>}
                    {isFieldVisible("Court Ref") && (
                      <td 
                        onClick={(e) => handleCourtRefClick(e, task.courtRef)}
                        
                      >
                        {task.courtRef} - (
                        {details?.caseData?.parties || "Not Available"})
                      </td>
                    )}
                    {isFieldVisible("Assigned By") && (
                      <td>
                        {details.loading
                          ? "Loading..."
                          : details.assignedByName || task.assignedBy}
                      </td>
                    )}
                    {isFieldVisible("Assigned To") && (
                      <td>
                        {details.loading
                          ? "Loading..."
                          : details.assignedToNames.length > 0
                          ? details.assignedToNames.join(", ")
                          : Array.isArray(task.assignedTo)
                          ? task.assignedTo.join(", ")
                          : task.assignedTo}
                      </td>
                    )}
                    {isFieldVisible("Branch") && (
                      <td>
                        {details.loading
                          ? "Loading..."
                          : details.branchName || task.branchId}
                      </td>
                    )}

                    {console.log(task?.description)}
                    {isFieldVisible("Description") && (
                      <td className="description-cell">
                        {task.description ? (
                          (() => {
                            let html = "";
                            try {
                              const raw =
                                typeof task.description === "string"
                                  ? JSON.parse(task.description)
                                  : task.description;
                              if (raw && raw.blocks) {
                                html = draftToHtml(raw);
                              } else {
                                html = task.description;
                              }
                            } catch (e) {
                              html = task.description;
                            }
                            // Fix anchor tags to always use absolute URLs
                            let fixedHtml = html.replace(
                              /<a ([^>]*href=["'])(?!https?:\/\/)([^"'>]+)(["'][^>]*)>/gi,
                              "<a $1https://$2$3>"
                            );

                            fixedHtml = fixedHtml.replace(
                              /<a /gi,
                              '<a target="_blank" rel="noopener noreferrer" '
                            );
                            return (
                              <div
                                dangerouslySetInnerHTML={{ __html: fixedHtml }}
                              />
                            );
                          })()
                        ) : (
                          <p>No description provided</p>
                        )}
                      </td>
                    )}

                    {isFieldVisible("Due Date") && (
                      <td>
                        {task.dueDate
                          ? new Date(task.dueDate).toLocaleString()
                          : "N/A"}
                      </td>
                    )}
                    {isFieldVisible("Status") && (
                      <td>
                        <span
                          className={`status-badge ${
                            task.isCompleted ? "completed" : "pending"
                          }`}
                        >
                          {task.isCompleted ? "Completed" : "Pending"}
                        </span>
                      </td>
                    )}
                    {isFieldVisible("Recurring Task") && (
                      <td>
                        {task.recurringTask
                          ? `${task.recurringTask.times} times per ${task.recurringTask.in}`
                          : "No"}
                      </td>
                    )}
                    {isFieldVisible("Document") && (
                      <td>
                        {task.document ? (
                          <a
                            href={task.document}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="document-link"
                          >
                            View Document
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    // Card view (original)
    return (
      <div className="ext-cal-tasks-list">
        {sortedTasks.map((task, index) => {
          const details = getTaskDetail(task._id);
          return (
            <div 
              key={task._id} 
              className="ext-cal-task-item"
            >
              <div className="ext-cal-hearing-section">
                <h4 className="ext-cal-hearing-section-title">
                  Task Details #{index + 1}
                </h4>
                {Object.keys(visibleFields.tasks).map((fieldName) =>
                  renderTaskField(fieldName, task, details, index)
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="ext-cal-container">
      <div className="ext-cal-header">
        <button
          className="ext-cal-btn ext-cal-btn-primary"
          onClick={handleTodayClick}
        >
          Today
        </button>
        <div className="display-board-expbtn">
          <button className="export-btn" onClick={handleExportClick}>
            Export to PDF
          </button>
        </div>
      </div>

      <div className="ext-cal-date-navigation">
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          dashboardData={{
            hearings: sortedHearings,
            tasks: sortedTasks,
            selectedDate: selectedDate,
            activeTab: activeTab,
            ...dashboardData, // Include the fetched dashboard data
          }}
        />
        <div className="ext-cal-month-controls">
          <select
            className="ext-cal-select"
            value={currentMonth}
            onChange={handleMonthChange}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i} value={i}>
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            ))}
          </select>
          <select
            className="ext-cal-select"
            value={currentYear}
            onChange={handleYearChange}
          >
            {Array.from({ length: 20 }, (_, i) => currentYear - 10 + i).map(
              (year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              )
            )}
          </select>
        </div>

        <div className="ext-cal-date-arrows">
          <button className="ext-cal-nav-btn" onClick={handlePrevDates}>
            <FiChevronLeft />
          </button>
          <button className="ext-cal-nav-btn" onClick={handleNextDates}>
            <FiChevronRight />
          </button>
        </div>
      </div>

      <div className="ext-cal-date-strip">
        {dates.map((date) => {
          const formatted = formatDate(date);
          const isSelected = selectedDate === formatted.fullDate;
          const hasData = fullData[formatted.fullDate];

          return (
            <div
              key={formatted.fullDate}
              className={`ext-cal-date-card ${
                isSelected ? "ext-cal-selected" : ""
              } ${hasData ? "ext-cal-has-data" : ""}`}
              onClick={() => handleDateSelect(formatted.fullDate)}
            >
              <div className="ext-cal-date-day">{formatted.day}</div>
              <div className="ext-cal-date-month">{formatted.month}</div>
              <div className="ext-cal-date-weekday">{formatted.weekday}</div>
              {hasData && <div className="ext-cal-date-dot"></div>}
            </div>
          );
        })}
      </div>

      <div className="ext-cal-controls">
        {/* Sorting Dropdown */}
        <select
          className="ext-cal-select"
          value={activeTab === "hearings" ? hearingSortBy : taskSortBy}
          onChange={(e) =>
            activeTab === "hearings"
              ? setHearingSortBy(e.target.value)
              : setTaskSortBy(e.target.value)
          }
        >
          {getSortOptions.length === 0 && (
            <option value="">No sort options available</option>
          )}
          {getSortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="ext-cal-tabs">
          <button
            className={`ext-cal-tab ${
              activeTab === "hearings" ? "ext-cal-tab-active" : ""
            }`}
            onClick={() => setActiveTab("hearings")}
          >
            Hearings
          </button>
          <button
            className={`ext-cal-tab ${
              activeTab === "tasks" ? "ext-cal-tab-active" : ""
            }`}
            onClick={() => setActiveTab("tasks")}
          >
            Tasks
          </button>
        </div>

        <div className="ext-cal-actions">
          {/* View Mode Toggle */}
          <div className="ext-cal-view-toggle">
            <button
              className={`ext-cal-btn ext-cal-btn-outline ${
                viewMode === "card" ? "ext-cal-btn-active" : ""
              }`}
              onClick={() => setViewMode("card")}
              title="Card View"
            >
              <IoGrid />
            </button>
            <button
              className={`ext-cal-btn ext-cal-btn-outline ${
                viewMode === "table" ? "ext-cal-btn-active" : ""
              }`}
              onClick={() => setViewMode("table")}
              title="Table View"
            >
              <FaTableList />
            </button>
          </div>

          <div className="ext-cal-options-dropdown" ref={dropdownRef}>
            <button
              className="ext-cal-btn ext-cal-btn-outline ext-cal-options-trigger"
              onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
            >
              <FiSettings />
              Options
              <FiChevronDown
                className={`ext-cal-dropdown-arrow ${
                  showOptionsDropdown ? "ext-cal-dropdown-arrow-open" : ""
                }`}
              />
            </button>

            {showOptionsDropdown && (
              <div className="ext-cal-dropdown-menu">
                <div className="ext-cal-dropdown-header">
                  <h4>
                    Display Options -{" "}
                    {activeTab === "hearings" ? "Hearings" : "Tasks"}
                  </h4>
                  <button
                    className="ext-cal-btn ext-cal-btn-outline ext-cal-btn-sm"
                    onClick={handleSelectAll}
                  >
                    {Object.values(visibleFields[activeTab]).every(
                      (value) => value
                    )
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                </div>
                <div className="ext-cal-dropdown-content">
                  {Object.keys(visibleFields[activeTab]).map((fieldName) => (
                    <label key={fieldName} className="ext-cal-field-option">
                      <input
                        type="checkbox"
                        checked={visibleFields[activeTab][fieldName]}
                        onChange={() => handleFieldToggle(fieldName)}
                      />
                      <span className="ext-cal-field-label">{fieldName}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="ext-cal-listing">
        {activeTab === "hearings" ? renderHearings() : renderTasks()}
      </div>
    </div>
  );
};

export default ExtendedViewPage;
